import { queryOptions } from "@tanstack/react-query";
import type { AuthFetchParams } from "./global";
import {
  platform_products_route,
  platforms_tiktok_alias,
  platforms_tiktok_oauth_url,
  platforms_tiktok_order_sync,
  VITE_API_URL,
} from "@/constants";
import type {
  PlatformProductsResponse,
  TiktokAccount,
  TiktokOrderSyncConfig,
} from "@/lib/types";
import type { InventoryFilters, ProductSort } from "./product";
import { DEFAULT_PRODUCT_SORT, serverSearch } from "./product";

// the API picks the US or rest of the world page from the org's other stores
export async function createTiktokConnectUrl(params: AuthFetchParams) {
  const res = await fetch(`${VITE_API_URL}${platforms_tiktok_oauth_url}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  return (await res.json()) as { url: string };
}

async function getProducts(
  pcId: string,
  params: AuthFetchParams,
  page: number,
  pageSize: number,
  filters: InventoryFilters,
  searchTerm = "",
  sort: ProductSort = DEFAULT_PRODUCT_SORT,
) {
  const search = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const trimmedSearchTerm = searchTerm.trim();
  if (trimmedSearchTerm) {
    search.set("search", trimmedSearchTerm);
  }
  search.set("sync_status", filters.sync);
  if (filters.pcIds.length > 0) {
    search.set("pc_ids", filters.pcIds.join(","));
  }
  if (filters.collectionIds.length > 0) {
    search.set("collection_ids", filters.collectionIds.join(","));
  }
  search.set("sort_by", sort.by);
  search.set("sort_dir", sort.dir);
  const res = await fetch(
    `${VITE_API_URL}${platform_products_route}/${pcId}?${search}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  return (await res.json()) as PlatformProductsResponse;
}

export async function getAccount(alias: string, params: AuthFetchParams) {
  const res = await fetch(
    `${VITE_API_URL}${platforms_tiktok_alias}/${encodeURIComponent(alias)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  if (!res.ok) {
    throw new Error("Failed to load tiktok account");
  }

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  return (await res.json()) as TiktokAccount;
}

export async function getTiktokOrderSyncConfig(
  pcId: string,
  params: AuthFetchParams,
) {
  const resp = await fetch(
    `${VITE_API_URL}${platforms_tiktok_order_sync}/${pcId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  return (await resp.json()) as TiktokOrderSyncConfig;
}

export const tiktokOrderSyncConfigQueryOpts = (
  pcId: string,
  params: AuthFetchParams,
) =>
  queryOptions({
    queryKey: ["tiktokOrderSyncConfig", pcId],
    queryFn: async () => await getTiktokOrderSyncConfig(pcId, params),
  });

export const tiktokAccountQueryOpts = (
  alias: string,
  params: AuthFetchParams,
) =>
  queryOptions({
    queryKey: ["tiktokAccount", alias],
    queryFn: async () => await getAccount(alias, params),
  });

export const tiktokProductListQueryOpts = (
  alias: string,
  params: AuthFetchParams,
  page = 1,
  pageSize = 30,
  filters: InventoryFilters = { sync: "all", pcIds: [], collectionIds: [] },
  searchTerm = "",
  sort: ProductSort = DEFAULT_PRODUCT_SORT,
) => {
  const search = serverSearch(searchTerm);
  return queryOptions({
    queryKey: ["tiktokProducts", alias, page, pageSize, filters, search, sort],
    queryFn: async () => {
      const tiktokUser = await getAccount(alias, params);
      return await getProducts(
        tiktokUser.platform_connection_id,
        params,
        page,
        pageSize,
        filters,
        search,
        sort,
      );
    },
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};
